import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClientProvider, QueryClient } from '@tanstack/react-query';
import { ThemeProvider } from "./contexts/ThemeContext";
import { AuthProvider } from './contexts/auth/AuthContext';
import { Toaster } from "@/components/ui/toaster";
import { CopilotProvider } from './contexts/copilot/CopilotProvider';
import { CommandPaletteProvider } from './contexts/command/CommandPaletteContext';
import ProtectedRoute from './utils/routeProtection';
import DashboardLayout from './components/layout/DashboardLayout';
import SignIn from './pages/auth/SignIn';
import SignUp from './pages/auth/SignUp';
import { lazy, Suspense } from 'react';
import NotFound from './pages/NotFound';

// Import CopilotKit styles
import '@copilotkit/react-ui/styles.css';

// Import the dashboard page
import Dashboard from './pages/dashboard/Dashboard';

// Import the lesson detail page
import LessonDetail from './pages/lessons/LessonDetail';

// Import the course builder
import CourseBuilder from './pages/admin/CourseBuilder';

// Import the modules pages
import ModulesList from './pages/modules/ModulesList';
import ModuleDetail from './pages/modules/ModuleDetail';

// Import Portfolio component
import Portfolio from './pages/portfolio/Portfolio';

// Import Admin components
import AdminDashboard from './pages/admin/AdminDashboard';
import UserManagement from './pages/admin/UserManagement';
import CreateUsers from './pages/admin/CreateUsers';
import MentorManagement from './pages/admin/MentorManagement';
import SystemSettings from './pages/admin/SystemSettings';
import ProjectManagement from './pages/admin/ProjectManagement';

// Import Mentor components
import MentorDashboard from './pages/mentor/MentorDashboard';
import MentorCourseBuilder from './pages/mentor/MentorCourseBuilder';
import MentorOfficeHours from './pages/mentor/MentorOfficeHours';
import MentorProjects from './pages/mentor/MentorProjects';
import MentorStudents from './pages/mentor/MentorStudents';

// Import Projects component
import ProjectsList from './pages/projects/ProjectsList';

// Import Agentic Dashboard components
import AgenticDashboard from './pages/agentic/AgenticDashboard';
import AgenticAgentsWrapper from './components/agentic/agents/AgenticAgentsWrapper';
import DevTodoPage from './pages/agentic/DevTodoPage';

// Import Workflow Pages
import WorkflowsPage from './pages/agentic/WorkflowsPage';

// Import Showcase Pages
import { ShowcaseGalleryPage, ShowcaseItemPage } from './pages/showcase';
import EditWorkflowPage from './pages/agentic/EditWorkflowPage';

// Use React.lazy to dynamically import components that aren't available yet
const Home = lazy(() => import('./pages/Index'));

// Placeholder for agentic components that will be implemented later
const AgenticPlaceholder = ({ name }: { name: string }) => (
  <div className="flex items-center justify-center h-screen">
    <div className="text-center">
      <h1 className="text-2xl font-bold mb-4">Agentic {name} Page</h1>
      <p>This page is coming soon</p>
    </div>
  </div>
);

// Placeholder components until actual ones are implemented
const Placeholder = ({ name }: { name: string }) => (
  <div className="flex items-center justify-center h-screen">
    <div className="text-center">
      <h1 className="text-2xl font-bold mb-4">{name} Page</h1>
      <p>This page is coming soon</p>
    </div>
  </div>
);

const queryClient = new QueryClient();

function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <CopilotProvider>
              <CommandPaletteProvider>
                <Suspense fallback={<div>Loading...</div>}>
                  <Routes>
                    {/* Public routes */}
                    <Route path="/" element={<Home />} />
                    <Route path="/auth/signin" element={<SignIn />} />
                    <Route path="/auth/signup" element={<SignUp />} />
                    
                    {/* Dashboard route */}
                    <Route 
                      path="/dashboard" 
                      element={
                        <ProtectedRoute>
                          <DashboardLayout>
                            <Dashboard />
                          </DashboardLayout>
                        </ProtectedRoute>
                      } 
                    />
                    
                    {/* Protected routes */}
                    <Route path="/portfolio" element={<ProtectedRoute><DashboardLayout><Portfolio /></DashboardLayout></ProtectedRoute>} />
                    <Route path="/modules" element={<ProtectedRoute><DashboardLayout><ModulesList /></DashboardLayout></ProtectedRoute>} />
                    <Route path="/modules/:id" element={<ProtectedRoute><DashboardLayout><ModuleDetail /></DashboardLayout></ProtectedRoute>} />
                    <Route path="/lessons/:id" element={<ProtectedRoute><DashboardLayout><LessonDetail /></DashboardLayout></ProtectedRoute>} />
                    <Route path="/projects" element={<ProtectedRoute><DashboardLayout><ProjectsList /></DashboardLayout></ProtectedRoute>} />
                    <Route path="/projects/:id" element={<ProtectedRoute><DashboardLayout><Placeholder name="Project Detail" /></DashboardLayout></ProtectedRoute>} />
                    <Route path="/projects/new" element={<ProtectedRoute><DashboardLayout><Placeholder name="New Project" /></DashboardLayout></ProtectedRoute>} />
                    <Route path="/personality" element={<ProtectedRoute><DashboardLayout><Placeholder name="Personality Assessment" /></DashboardLayout></ProtectedRoute>} />
                    <Route path="/profile" element={<ProtectedRoute><DashboardLayout><Placeholder name="Personality Profile" /></DashboardLayout></ProtectedRoute>} />
                    <Route path="/office-hours" element={<ProtectedRoute><DashboardLayout><Placeholder name="Office Hours List" /></DashboardLayout></ProtectedRoute>} />
                    <Route path="/office-hours/:id" element={<ProtectedRoute><DashboardLayout><Placeholder name="Office Hours Detail" /></DashboardLayout></ProtectedRoute>} />
                    <Route path="/second-me" element={<ProtectedRoute><DashboardLayout><Placeholder name="Second ME" /></DashboardLayout></ProtectedRoute>} />
                    
                    {/* Showcase routes */}
                    <Route path="/showcase" element={<ProtectedRoute><DashboardLayout><ShowcaseGalleryPage /></DashboardLayout></ProtectedRoute>} />
                    <Route path="/showcase/:id" element={<ProtectedRoute><DashboardLayout><ShowcaseItemPage /></DashboardLayout></ProtectedRoute>} />
                    
                    {/* Mentor routes */}
                    <Route path="/mentor" element={<ProtectedRoute requiredRoles={['mentor']}><DashboardLayout><MentorDashboard /></DashboardLayout></ProtectedRoute>} />
                    <Route path="/mentor/course-builder" element={<ProtectedRoute requiredRoles={['mentor']}><DashboardLayout><MentorCourseBuilder /></DashboardLayout></ProtectedRoute>} />
                    <Route path="/mentor/office-hours" element={<ProtectedRoute requiredRoles={['mentor']}><DashboardLayout><MentorOfficeHours /></DashboardLayout></ProtectedRoute>} />
                    <Route path="/mentor/projects" element={<ProtectedRoute requiredRoles={['mentor']}><DashboardLayout><MentorProjects /></DashboardLayout></ProtectedRoute>} />
                    <Route path="/mentor/projects/:id" element={<ProtectedRoute requiredRoles={['mentor']}><DashboardLayout><Placeholder name="Mentor Project Review" /></DashboardLayout></ProtectedRoute>} />
                    <Route path="/mentor/students" element={<ProtectedRoute requiredRoles={['mentor']}><DashboardLayout><MentorStudents /></DashboardLayout></ProtectedRoute>} />
                    
                    {/* Admin routes */}
                    <Route path="/admin" element={<ProtectedRoute requiredRoles={['admin']}><DashboardLayout><AdminDashboard /></DashboardLayout></ProtectedRoute>} />
                    <Route path="/admin/users" element={<ProtectedRoute requiredRoles={['admin']}><DashboardLayout><UserManagement /></DashboardLayout></ProtectedRoute>} />
                    <Route path="/admin/users/create" element={<ProtectedRoute requiredRoles={['admin']}><DashboardLayout><CreateUsers /></DashboardLayout></ProtectedRoute>} />
                    <Route path="/admin/course-builder" element={<ProtectedRoute requiredRoles={['admin']}><DashboardLayout><CourseBuilder /></DashboardLayout></ProtectedRoute>} />
                    <Route path="/admin/mentors" element={<ProtectedRoute requiredRoles={['admin']}><DashboardLayout><MentorManagement /></DashboardLayout></ProtectedRoute>} />
                    <Route path="/admin/projects" element={<ProtectedRoute requiredRoles={['admin']}><DashboardLayout><ProjectManagement /></DashboardLayout></ProtectedRoute>} />
                    <Route path="/admin/projects/:id" element={<ProtectedRoute requiredRoles={['admin']}><DashboardLayout><Placeholder name="Admin Project Detail" /></DashboardLayout></ProtectedRoute>} />
                    <Route path="/admin/settings" element={<ProtectedRoute requiredRoles={['admin']}><DashboardLayout><SystemSettings /></DashboardLayout></ProtectedRoute>} />
                    <Route path="/admin/office-hours" element={<ProtectedRoute requiredRoles={['admin']}><DashboardLayout><Placeholder name="Admin Office Hours" /></DashboardLayout></ProtectedRoute>} />
                    <Route path="/admin/office-hours/create" element={<ProtectedRoute requiredRoles={['admin']}><DashboardLayout><Placeholder name="Create Office Hours" /></DashboardLayout></ProtectedRoute>} />
                    <Route path="/admin/office-hours/:id" element={<ProtectedRoute requiredRoles={['admin']}><DashboardLayout><Placeholder name="Office Hours Details" /></DashboardLayout></ProtectedRoute>} />
                    
                    {/* Agentic Dashboard routes */}
                    <Route path="/agentic" element={<ProtectedRoute><DashboardLayout><AgenticDashboard /></DashboardLayout></ProtectedRoute>} />
                    
                    {/* Workflows routes - Replace placeholders with actual components */}
                    <Route path="/agentic/workflows" element={<ProtectedRoute><DashboardLayout><WorkflowsPage /></DashboardLayout></ProtectedRoute>} />
                    <Route path="/agentic/workflows/edit/:id" element={<ProtectedRoute><DashboardLayout><EditWorkflowPage /></DashboardLayout></ProtectedRoute>} />
                    
                    <Route path="/agentic/capabilities" element={<ProtectedRoute><DashboardLayout><AgenticPlaceholder name="Capabilities" /></DashboardLayout></ProtectedRoute>} />
                    <Route path="/agentic/prompts" element={<ProtectedRoute><DashboardLayout><AgenticPlaceholder name="Prompt Library" /></DashboardLayout></ProtectedRoute>} />
                    <Route path="/agentic/executions" element={<ProtectedRoute><DashboardLayout><AgenticPlaceholder name="Execution History" /></DashboardLayout></ProtectedRoute>} />
                    <Route path="/agentic/executions/:id" element={<ProtectedRoute><DashboardLayout><AgenticPlaceholder name="Execution Detail" /></DashboardLayout></ProtectedRoute>} />
                    <Route path="/agentic/agents" element={<ProtectedRoute><DashboardLayout><AgenticAgentsWrapper /></DashboardLayout></ProtectedRoute>} />
                    <Route path="/agentic/dev-todo" element={<ProtectedRoute><DashboardLayout><DevTodoPage /></DashboardLayout></ProtectedRoute>} />
                    
                    {/* Handle 404 - Not Found */}
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </Suspense>
                <Toaster />
              </CommandPaletteProvider>
            </CopilotProvider>
          </AuthProvider>
        </QueryClientProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
